import React, { useEffect, useRef } from 'react';
import { MdClose, MdPlaylistRemove } from 'react-icons/md';

import { isNoteValid } from './utils/parecerInterruption';

/**
 * AF-4082: modal da OBSERVAÇÃO EM LOTE, aplicada aos itens selecionados do parecer.
 *
 * A interface recebe UMA mensagem pré-preenchida e editável (um único textarea), aplicada a TODOS os itens
 * marcados. A mensagem é obrigatória: o botão de confirmar fica desabilitado enquanto
 * o texto estiver vazio. Segue o padrão visual de TriggeredRulesModal (overlay, dark mode,
 * fecha no backdrop e no Escape).
 */
interface InterruptionNoteModalProps {
    open: boolean;
    selectedCount: number;
    note: string;
    confirming?: boolean;
    onNoteChange: (value: string) => void;
    onConfirm: () => void;
    onClose: () => void;
}

const InterruptionNoteModal: React.FC<InterruptionNoteModalProps> = ({
    open,
    selectedCount,
    note,
    confirming = false,
    onNoteChange,
    onConfirm,
    onClose,
}) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        if (!open) return;
        textareaRef.current?.focus();

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open, onClose]);

    if (!open) return null;

    const valid = isNoteValid(note);
    const showError = note.length > 0 && !valid;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) onClose();
            }}
        >
            <div
                className="flex w-full max-w-lg flex-col overflow-hidden rounded-lg bg-white shadow-xl dark:bg-slate-800"
                role="dialog"
                aria-modal="true"
                aria-labelledby="interruption-modal-title"
            >
                <header className="flex items-start gap-3 border-b border-gray-200 px-5 py-4 dark:border-slate-700">
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600 dark:bg-slate-700 dark:text-amber-400">
                        <MdPlaylistRemove size={20} aria-hidden="true" />
                    </span>
                    <div className="min-w-0 flex-1">
                        <h3
                            id="interruption-modal-title"
                            className="text-base font-semibold text-gray-900 dark:text-gray-100"
                        >
                            Observação em lote
                        </h3>
                        <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">
                            A mensagem será acrescentada às observações de {selectedCount}{' '}
                            {selectedCount === 1 ? 'item selecionado' : 'itens selecionados'}.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-slate-700 dark:hover:text-gray-200"
                        aria-label="Fechar"
                    >
                        <MdClose size={20} />
                    </button>
                </header>

                <div className="px-5 py-4">
                    <label
                        htmlFor="interruption-note"
                        className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200"
                    >
                        Observação
                    </label>
                    <textarea
                        id="interruption-note"
                        ref={textareaRef}
                        value={note}
                        onChange={(event) => onNoteChange(event.target.value)}
                        rows={4}
                        placeholder="Descreva a observação para os itens selecionados"
                        className={`block w-full rounded-md border px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-1 dark:bg-slate-700 dark:text-white dark:placeholder:text-gray-500 ${
                            showError
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500 dark:border-red-500 dark:focus:text-red-400'
                                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:focus:text-blue-400'
                        }`}
                    />
                    {showError && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                            Informe a observação para os itens selecionados.
                        </p>
                    )}
                </div>

                <footer className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3 dark:border-slate-700">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:text-gray-200 dark:hover:bg-slate-700"
                    >
                        Voltar
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={!valid || confirming}
                        className="rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {confirming ? 'Aplicando...' : 'Aplicar observação'}
                    </button>
                </footer>
            </div>
        </div>
    );
};

export default InterruptionNoteModal;
